// Adapted from eval/dashboard/internal_web/src/components/session/SlopeChart.tsx (ATM-57): no ground-truth layer. Keep in step until ATM-60 shares it.
/**
 * The answer's impact as a slope chart — the eval dashboard's `SlopeChart`, without the
 * ground-truth layer a live session has no truth for.
 *
 * Each curve runs from a disease's rank after the previous stored belief (left) to its rank now
 * (right), so a disease this answer lifted from seventh to first draws a steep curve and one it
 * left alone runs flat. A newcomer rises from the "below" slot — on the first answer every
 * disease does, since nothing was ranked before it; what left the top falls into it, dashed,
 * and is named.
 *
 * **It moves, it is not redrawn.** Stepping to the next answer — or a new answer landing while
 * the page follows the live session — glides every curve and every row to its new rank over the
 * same 300ms, so the reader follows each disease rather than re-reading the chart. The rows sit
 * in a stable DOM order and are placed by transform, because a reordered DOM node loses its
 * transition; the order a screen reader needs is a separate rank-ordered list.
 */

import type { DiseaseSnapshot } from "../../api/types";
import { cn } from "../../lib/cn";
import { ordinal, pctBounded } from "../../lib/format";
import InfoTip from "../ui/InfoTip";
import {
  entryChanges,
  firstByName,
  pointsLabel,
  slopeLines,
  type EntryChange,
  type SlopeLine,
} from "./beliefChange";
import { STEP_MS, useTweenedLines, type LinePosition } from "./useSlopeTween";

/** How many diseases the chart ranks: ten, the rest are on the panel's "All ranked" tab. */
export const DEPTH = 10;

/** The value columns' widths, shared by the header and every row so they line up. */
const COLUMN = { likelihood: "w-14", change: "w-11", confidence: "w-11" };

/** One slot's height: a rank on each side, and the "below the top" slot under them. */
const SLOT = 30;
const WIDTH = 56;
/** Where a curve leaves and arrives, inset so its end dots are not clipped. */
const X0 = 4;
const X1 = WIDTH - 4;

function y(slot: number): number {
  return slot * SLOT + SLOT / 2;
}

/** A smooth S-curve between two slots — flat at both ends, so a flat line stays flat. */
function curve({ from, to }: LinePosition): string {
  const mid = (X0 + X1) / 2;
  return `M ${X0} ${y(from)} C ${mid} ${y(from)}, ${mid} ${y(to)}, ${X1} ${y(to)}`;
}

/** Heavier where the answer moved something, faint where it did not, dashed where it left. */
function stroke(line: SlopeLine): { color: string; width: number; dash?: string } {
  if (line.kind === "left") return { color: "var(--ink-faint)", width: 1, dash: "3 3" };
  if (line.kind === "entered") return { color: "var(--meter-likelihood)", width: 1.75 };
  if (line.from === line.to) return { color: "var(--hairline-agent)", width: 1.25 };
  return { color: "var(--ink-2)", width: 1.75 };
}

/** How an entry got here, in words: the row's hover text and its screen-reader tail. */
function howItMoved(entry: DiseaseSnapshot, change: EntryChange | undefined): string {
  if (change === undefined) return "";
  if (change.kind === "new") return "new to the ranking";
  return change.rankDelta === 0 ? "unchanged" : `was ${ordinal(entry.rank + change.rankDelta)}`;
}

/**
 * Where a disease that is no longer in the stored ranking went. Snapshots are cut off at a
 * fixed depth (20, and 5 before 2026-09-07), so "not in it" means "below its last entry",
 * not "ruled out".
 */
function belowWords(depth: number): string {
  return depth > 0 ? `below ${ordinal(depth)}` : "not ranked";
}

/** The value columns' header, and what the three numbers measure. */
function ValueHeads() {
  return (
    <div className="flex h-4 items-center justify-end gap-2 text-[11px] leading-4 text-ink-faint">
      <InfoTip label="What the columns measure" align="end">
        <p>
          <strong className="font-medium">Likelihood</strong> is a product of the authored weights
          on the graph edges this disease matched, normalised across the candidates. It saturates,
          so read it as an ordering.
        </p>
        <p>
          <strong className="font-medium">Δ</strong> is its change since the previous stored
          belief, in percentage points. A question med-reasoning answered itself in between is
          folded in.
        </p>
        <p>
          <strong className="font-medium">Confidence</strong> is the share of the graph&rsquo;s
          evidence for this disease the interview has covered. It rises the same on a yes as on a
          no — read it as a floor.
        </p>
      </InfoTip>
      <span className={cn(COLUMN.likelihood, "text-right")}>Likelihood</span>
      <span className={cn(COLUMN.change, "text-right")}>Δ pts</span>
      <span className={cn(COLUMN.confidence, "text-right")}>Conf.</span>
    </div>
  );
}

/** One disease's row: rank, name, and its numbers. */
function Row({ entry, change }: { entry: DiseaseSnapshot; change: EntryChange | undefined }) {
  const points = change?.kind === "moved" ? pointsLabel(change.likelihoodDelta) : null;
  const moved = howItMoved(entry, change);
  return (
    <div
      className="flex h-full min-w-0 items-center gap-2 border-b border-hairline pl-2"
      title={`${entry.disease_name}\nRank ${entry.rank}${moved === "" ? "" : `, ${moved}`}`}
    >
      <span className="w-4 shrink-0 text-[11px] tabular-nums text-ink-faint">{entry.rank}</span>
      <span className="truncate text-[13px] text-ink">{entry.disease_name}</span>
      <span className="ml-auto flex shrink-0 items-baseline gap-2 tabular-nums">
        <span className={cn(COLUMN.likelihood, "text-right text-[13px] text-ink")}>
          {pctBounded(entry.likelihood)}
        </span>
        <span className={cn(COLUMN.change, "text-right text-[11px] text-ink-3")}>{points ?? ""}</span>
        <span className={cn(COLUMN.confidence, "text-right text-[12px] text-ink-3")}>
          {pctBounded(entry.confidence)}
        </span>
      </span>
    </div>
  );
}

/** What fell out of the top, in the "below" slot. */
function leftSentence(left: readonly SlopeLine[], compared: boolean, depth: number): string {
  if (!compared) return "";
  if (left.length === 0) return `Nothing left the top ${DEPTH}`;
  return `Left the top ${DEPTH}: ${left
    .map((line) =>
      line.outsideRank === null
        ? `${line.name} (${belowWords(depth)})`
        : `${line.name} → ${ordinal(line.outsideRank)}`,
    )
    .join(", ")}`;
}

/** The curves alone, so the per-frame tween re-renders only them and not every row beside them. */
function SlopeCurves({ lines }: { lines: readonly SlopeLine[] }) {
  const drawn = useTweenedLines(
    new Map(lines.map((line) => [line.name, { from: line.from, to: line.to }])),
  );
  return (
    <svg width={WIDTH} height={(DEPTH + 1) * SLOT} className="overflow-visible" aria-hidden>
      {lines.map((line) => {
        const at = drawn.get(line.name) ?? line;
        const { color, width, dash } = stroke(line);
        return (
          <g key={line.name} className="animate-fade-in motion-reduce:animate-none">
            <path
              d={curve(at)}
              fill="none"
              stroke={color}
              strokeWidth={width}
              strokeDasharray={dash}
              strokeLinecap="round"
            />
            {line.kind === "left" ? null : (
              <>
                <circle cx={X0} cy={y(at.from)} r={2.25} fill={color} />
                <circle cx={X1} cy={y(at.to)} r={2.25} fill={color} />
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

type Props = {
  /** The whole stored ranking for this answer; the chart shows its top `DEPTH`. */
  ranking: readonly DiseaseSnapshot[];
  /** The previous stored belief's whole ranking, or `null` for the first. */
  previous: readonly DiseaseSnapshot[] | null;
};

export default function SlopeChart({ ranking, previous }: Props) {
  const shown = ranking.slice(0, DEPTH);
  // The first belief slopes from nothing: every disease enters from below, as a newcomer does
  // later, so the chart has the same shape on every answer and the next step glides out of it.
  const lines = slopeLines(ranking, previous ?? [], DEPTH);
  const changes = previous === null ? null : entryChanges(shown, previous);
  const left = lines.filter((line) => line.kind === "left");
  // One row per disease in a STABLE order: rows are placed by transform, and a node React
  // reorders would lose its transition.
  const byName = firstByName(shown);
  const slotOf = new Map([...byName.keys()].map((name, slot) => [name, slot] as const));
  const stable = [...byName.keys()].sort((a, b) => a.localeCompare(b));

  return (
    <div className="flex flex-col gap-1">
      <ValueHeads />
      <div className="grid grid-cols-[1.5rem_3.5rem_minmax(0,1fr)]">
        {/* Where each curve starts: the previous belief's ranks, and "below". */}
        <ol className="flex flex-col" aria-hidden>
          {Array.from({ length: DEPTH + 1 }, (_, slot) => (
            <li
              key={slot}
              className="flex items-center text-[11px] tabular-nums text-ink-faint"
              style={{ height: SLOT }}
            >
              {slot < DEPTH ? slot + 1 : `${DEPTH + 1}+`}
            </li>
          ))}
        </ol>
        <SlopeCurves lines={lines} />
        {/* Where each curve ends: this answer's ranking, placed by transform so it glides. */}
        <div className="relative min-w-0" style={{ height: (DEPTH + 1) * SLOT }} aria-hidden>
          {stable.map((name) => {
            const entry = byName.get(name);
            const slot = slotOf.get(name);
            if (entry === undefined || slot === undefined) return null;
            return (
              <div
                key={name}
                className="absolute inset-x-0 top-0 animate-fade-in transition-transform ease-out motion-reduce:animate-none motion-reduce:transition-none"
                style={{
                  height: SLOT,
                  transform: `translateY(${slot * SLOT}px)`,
                  transitionDuration: `${STEP_MS}ms`,
                }}
              >
                <Row entry={entry} change={changes?.get(name)} />
              </div>
            );
          })}
          <p
            className="absolute inset-x-0 top-0 flex items-center truncate pl-2 text-[11px] text-ink-3"
            style={{ height: SLOT, transform: `translateY(${DEPTH * SLOT}px)` }}
            title={left
              .map((line) =>
                line.outsideRank === null
                  ? `${line.name}: ${belowWords(ranking.length)}`
                  : `${line.name}: now ${ordinal(line.outsideRank)}`,
              )
              .join("\n")}
          >
            <span className="truncate">{leftSentence(left, previous !== null, ranking.length)}</span>
          </p>
        </div>
      </div>
      {/* The same ranking in rank order, for a screen reader: the visual rows are not. */}
      <ol className="sr-only">
        {shown.map((entry) => {
          const change = changes?.get(entry.disease_name);
          return (
            <li key={`${entry.rank}-${entry.disease_name}`}>
              {entry.rank}. {entry.disease_name}, likelihood {pctBounded(entry.likelihood)},
              confidence {pctBounded(entry.confidence)}
              {change === undefined ? "" : `, ${howItMoved(entry, change)}`}.
            </li>
          );
        })}
        {left.length > 0 ? <li>{leftSentence(left, true, ranking.length)}</li> : null}
      </ol>
    </div>
  );
}
